import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FindInPageOutlinedIcon from '@mui/icons-material/FindInPageOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, useGridApiRef, type GridColDef } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  strapiGetLaboratoryWorkOrderPreview,
  strapiListLaboratoryResultGridRows,
  type LaboratoryResultWorkOrderPreviewData,
  type StrapiLaboratoryResultGridRow,
} from '../../../../api/strapiCrm';
import { useAuth } from '../../../../auth/AuthContext';
import { ChemInline, ChemParameterLabel } from '../../../../components/ChemInline';
import {
  DataGridNumberedPagination,
  DATA_GRID_DEFAULT_PAGE_SIZE,
  DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS,
} from '../../../../components/DataGridNumberedPagination';
import { queryKeys } from '../../../../query/queryKeys';
import { CollapsibleListFilters } from '../../../../ui/CollapsibleListFilters';
import { LIST_FILTERS_APPLY_HINT, commitListFiltersOnEnter } from '../../../../ui/listFiltersApi';
import { LaboratoryResultEditDialog, type LaboratoryResultEditMode } from '../../components/LaboratoryResultEditDialog';
import { LaboratorySamplingSnapshot } from '../../LaboratorySamplingSnapshot';
import {
  EMPTY_LABORATORY_RESULT_LIST_FILTERS,
  formatLaboratoryGridDate,
  formatLaboratoryResultJobNumber,
  isAdminAnalisLabUser,
  laboratoryResultListFiltersActive,
  lockedAnalisTypeFilter,
  UNASSIGNED_ANALIS_TYPE_FILTER,
  withLockedAnalisTypeFilter,
  type LaboratoryResultListFilters,
} from '../../laboratoryResultGridRows';
import { hasLaboratorySamplingSnapshot } from '../../laboratorySamplingSnapshotDisplay';

export function LaboratoryResultListTabScreen() {
  const { jwt, user, loading: authLoading } = useAuth();
  const hideAnalisLabRowEdit = isAdminAnalisLabUser(user);
  const lockedAnalisTypeKey = (user?.analisTypeDataTitles ?? []).join('\n');
  const lockedAnalisTypes = useMemo(
    () => (authLoading ? null : lockedAnalisTypeFilter(user)),
    [authLoading, lockedAnalisTypeKey, user],
  );
  const assignedAnalisTypeLabel = (lockedAnalisTypes ?? [])
    .filter((title) => title !== UNASSIGNED_ANALIS_TYPE_FILTER)
    .join(', ');
  const qc = useQueryClient();
  const gridApiRef = useGridApiRef();
  const [previewWoDocId, setPreviewWoDocId] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<LaboratoryResultWorkOrderPreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(() => new Set());
  const [editTarget, setEditTarget] = useState<{
    mode: LaboratoryResultEditMode;
    row: StrapiLaboratoryResultGridRow;
  } | null>(null);
  const [draftFilters, setDraftFilters] = useState<LaboratoryResultListFilters>(
    EMPTY_LABORATORY_RESULT_LIST_FILTERS,
  );
  const [appliedFilters, setAppliedFilters] = useState<LaboratoryResultListFilters>(
    EMPTY_LABORATORY_RESULT_LIST_FILTERS,
  );

  const rowsQ = useQuery({
    queryKey: queryKeys.laboratoryResultGridRows(jwt, appliedFilters),
    queryFn: () => strapiListLaboratoryResultGridRows(jwt!, appliedFilters),
    enabled:
      Boolean(jwt) &&
      !authLoading &&
      (lockedAnalisTypes == null ||
        appliedFilters.analisTypes.join('\n') === lockedAnalisTypes.join('\n')),
    // Manual refresh only (Muat ulang / setelah simpan) — no periodic or focus refetch.
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (authLoading) return;
    setDraftFilters((current) => withLockedAnalisTypeFilter(current, lockedAnalisTypes));
    setAppliedFilters((current) => withLockedAnalisTypeFilter(current, lockedAnalisTypes));
  }, [authLoading, lockedAnalisTypes]);

  const commitFilters = useCallback(() => {
    setAppliedFilters(withLockedAnalisTypeFilter(draftFilters, lockedAnalisTypes));
  }, [draftFilters, lockedAnalisTypes]);

  const resetFilters = useCallback(() => {
    const cleared = withLockedAnalisTypeFilter(EMPTY_LABORATORY_RESULT_LIST_FILTERS, lockedAnalisTypes);
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
  }, [lockedAnalisTypes]);

  const openPreview = useCallback(async (workOrderDocumentId: string) => {
    if (!jwt) return;
    setPreviewWoDocId(workOrderDocumentId);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewData(null);
    try {
      const data = await strapiGetLaboratoryWorkOrderPreview(jwt, workOrderDocumentId);
      setPreviewData(data);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Gagal memuat preview.');
    } finally {
      setPreviewLoading(false);
    }
  }, [jwt]);

  const toggleExpanded = useCallback((rowKey: string) => {
    setExpandedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const rows = rowsQ.data?.rows ?? [];
  const rowCount = rowsQ.data?.rowCount ?? rows.length;
  const unfilteredRowCount = rowsQ.data?.unfilteredRowCount ?? rows.length;
  const filtersActive = laboratoryResultListFiltersActive(appliedFilters);

  const openWorkOrderEdit = useCallback((row: StrapiLaboratoryResultGridRow) => {
    setEditTarget({ mode: 'workOrder', row });
  }, []);

  const openParameterEdit = useCallback((row: StrapiLaboratoryResultGridRow) => {
    setEditTarget({ mode: 'parameter', row });
  }, []);

  const relatedParameterRows = useMemo(() => {
    if (!editTarget || editTarget.mode !== 'parameter') return [] as StrapiLaboratoryResultGridRow[];
    const paramId = editTarget.row.parameter_uji_document_id?.trim();
    if (!paramId) return [editTarget.row];
    return rows.filter((r) => r.parameter_uji_document_id === paramId);
  }, [editTarget, rows]);

  useEffect(() => {
    const api = gridApiRef.current;
    if (!api || typeof api.resetRowHeights !== 'function') return;
    const frame = window.requestAnimationFrame(() => {
      api.resetRowHeights();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [expandedRowKeys, gridApiRef, rows]);

  const columns: GridColDef<StrapiLaboratoryResultGridRow>[] = useMemo(
    () => [
      {
        field: 'expand',
        headerName: '',
        width: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const expanded = expandedRowKeys.has(row.row_key);
          return (
            <Tooltip title={expanded ? 'Sembunyikan isian petugas sampling' : 'Lihat isian petugas sampling'}>
              <IconButton
                size="small"
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? `Sembunyikan isian sampling ${row.parameter_name}`
                    : `Lihat isian sampling ${row.parameter_name}`
                }
                data-testid={`laboratory-result-expand-${row.row_key}`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(row.row_key);
                }}
              >
                {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'work_order_job_number',
        headerName: 'Job number',
        minWidth: 220,
        flex: 1.2,
        valueGetter: (_, row) => formatLaboratoryResultJobNumber(row),
        renderCell: ({ row }) => {
          const expanded = expandedRowKeys.has(row.row_key);
          return (
            <Box
              sx={{
                py: 0.75,
                width: '100%',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                overflow: 'hidden',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  lineHeight: 1.35,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: expanded ? 'normal' : 'nowrap',
                }}
              >
                {formatLaboratoryResultJobNumber(row)}
              </Typography>
              {expanded ? (
                <Box
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {row.point_label}
                    {row.location_name ? ` · ${row.location_name}` : ''}
                  </Typography>
                  {hasLaboratorySamplingSnapshot(row.sampling) ? (
                    <Box
                      sx={{
                        '& > .MuiPaper-root': { mb: 0 },
                        width: '100%',
                        maxWidth: '100%',
                        overflowX: 'auto',
                      }}
                    >
                      <LaboratorySamplingSnapshot sampling={row.sampling} />
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Tidak ada isian petugas sampling.
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'parameter_name',
        headerName: 'Nama parameter',
        minWidth: 200,
        flex: 1,
        renderCell: ({ row }) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.25,
              width: '100%',
              py: 0.25,
            }}
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <Box
              component="span"
              sx={{
                display: 'inline',
                flex: 1,
                minWidth: 0,
                py: 0.5,
                wordBreak: 'break-word',
                '& .katex': { fontSize: 'inherit' },
                '& .katex-html': { whiteSpace: 'normal' },
                '& sub, & sup': { fontSize: '0.75em', lineHeight: 0 },
              }}
            >
              <ChemParameterLabel nama={row.parameter_name} simbol={row.parameter_simbol} />
            </Box>
            <Tooltip title="Isi parameter ini di semua titik / job number">
              <IconButton
                size="small"
                aria-label={`Isi parameter ${row.parameter_name} lintas titik`}
                data-testid={`laboratory-result-edit-parameter-${row.row_key}`}
                onClick={() => openParameterEdit(row)}
                sx={{ mt: 0.15 }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'result_filled',
        headerName: 'Sudah Diisi?',
        width: 110,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_, row) => (row.result?.trim() ? 1 : 0),
        renderCell: ({ row }) => {
          const filled = Boolean(row.result?.trim());
          return (
            <Tooltip title={filled ? 'Sudah diisi' : 'Belum diisi'}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
                data-testid={`laboratory-result-filled-${row.row_key}`}
                data-filled={filled ? 'true' : 'false'}
                aria-label={filled ? 'Sudah diisi' : 'Belum diisi'}
              >
                {filled ? (
                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'success.main' }} />
                ) : (
                  <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'rps_approved_datetime',
        headerName: 'Approved RPS date',
        minWidth: 160,
        flex: 0.8,
        valueGetter: (_, row) => row.rps_approved_datetime ?? '',
        valueFormatter: (value) => formatLaboratoryGridDate(typeof value === 'string' ? value : null),
      },
      {
        field: 'testing_done_datetime',
        headerName: 'Tanggal selesai pengujian',
        minWidth: 180,
        flex: 0.8,
        valueGetter: (_, row) => formatLaboratoryGridDate(row.testing_done_datetime),
      },
      {
        field: 'baku_mutu',
        headerName: 'Baku mutu',
        minWidth: 160,
        flex: 1,
        valueGetter: (_, row) => row.baku_mutu || '—',
      },
      {
        field: 'jenis_sampel',
        headerName: 'Jenis sampel',
        minWidth: 140,
        flex: 0.8,
        valueGetter: (_, row) => row.jenis_sampel || '—',
      },
      {
        field: 'actions',
        headerName: 'Aksi',
        width: 96,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const isReady = row.status === 'ready';
          const editLabel = isReady ? 'Lihat / ubah' : 'Isi hasil';
          const showRowEdit = isReady || !hideAnalisLabRowEdit;
          return (
            <Box
              sx={{ display: 'flex', gap: 0.25, alignItems: 'center' }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {showRowEdit ? (
                <Tooltip title={editLabel}>
                  <IconButton
                    size="small"
                    aria-label={`${editLabel} ${row.work_order_job_number || row.work_order_document_id}`}
                    data-testid={`laboratory-result-edit-${row.row_key}`}
                    onClick={() => openWorkOrderEdit(row)}
                  >
                    {isReady ? (
                      <VisibilityOutlinedIcon fontSize="small" />
                    ) : (
                      <EditOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title="Preview">
                <span>
                  <IconButton
                    size="small"
                    disabled={!isReady}
                    aria-label={`Preview ${row.work_order_job_number || row.work_order_document_id}`}
                    onClick={() => void openPreview(row.work_order_document_id)}
                  >
                    <FindInPageOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [expandedRowKeys, hideAnalisLabRowEdit, openParameterEdit, openPreview, openWorkOrderEdit, toggleExpanded],
  );

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <CollapsibleListFilters
        filterToggleTestId="laboratory-results-filter-toggle"
        defaultOpen
        actions={
          <Button
            variant="outlined"
            color="inherit"
            startIcon={rowsQ.isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            disabled={!jwt || rowsQ.isFetching}
            onClick={() => void rowsQ.refetch()}
            data-testid="laboratory-results-refresh-button"
          >
            Muat ulang
          </Button>
        }
      >
        <Typography variant="caption" color="text.secondary" display="block">
          {LIST_FILTERS_APPLY_HINT}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            alignItems: 'flex-start',
          }}
        >
          <TextField
            label="Job number"
            value={draftFilters.jobNumber}
            onChange={(e) => setDraftFilters((d) => ({ ...d, jobNumber: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'laboratory-results-filter-job-number' }}
          />
          <TextField
            label="Nama parameter"
            value={draftFilters.parameterName}
            onChange={(e) => setDraftFilters((d) => ({ ...d, parameterName: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'laboratory-results-filter-parameter-name' }}
          />
          <TextField
            label="Approved RPS date"
            value={draftFilters.rpsApprovedDate}
            onChange={(e) => setDraftFilters((d) => ({ ...d, rpsApprovedDate: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Contoh: 08/09/2026"
            inputProps={{ 'data-testid': 'laboratory-results-filter-rps-approved-date' }}
          />
          <TextField
            label="Tanggal selesai"
            value={draftFilters.testingDoneDate}
            onChange={(e) => setDraftFilters((d) => ({ ...d, testingDoneDate: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            helperText="Contoh: 09/09/2026"
            inputProps={{ 'data-testid': 'laboratory-results-filter-testing-done-date' }}
          />
          <TextField
            label="Baku mutu"
            value={draftFilters.bakuMutu}
            onChange={(e) => setDraftFilters((d) => ({ ...d, bakuMutu: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'laboratory-results-filter-baku-mutu' }}
          />
          <TextField
            label="Jenis sampel"
            value={draftFilters.jenisSampel}
            onChange={(e) => setDraftFilters((d) => ({ ...d, jenisSampel: e.target.value }))}
            onBlur={commitFilters}
            onKeyDown={commitListFiltersOnEnter(commitFilters)}
            size="small"
            fullWidth
            inputProps={{ 'data-testid': 'laboratory-results-filter-jenis-sampel' }}
          />
          {hideAnalisLabRowEdit ? (
            <TextField
              label="Analis type"
              value={assignedAnalisTypeLabel}
              size="small"
              fullWidth
              disabled
              helperText={
                assignedAnalisTypeLabel
                  ? 'Hanya parameter dengan analis type ini.'
                  : 'Analis type belum di-assign ke user ini.'
              }
              inputProps={{ 'data-testid': 'laboratory-results-filter-analis-type' }}
            />
          ) : null}
        </Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Typography variant="caption" color="text.secondary">
            {filtersActive
              ? `${rowCount} dari ${unfilteredRowCount} baris`
              : `${unfilteredRowCount} baris`}
          </Typography>
          <Button
            size="small"
            color="inherit"
            disabled={
              ![
                draftFilters.jobNumber,
                draftFilters.parameterName,
                draftFilters.rpsApprovedDate,
                draftFilters.testingDoneDate,
                draftFilters.bakuMutu,
                draftFilters.jenisSampel,
                appliedFilters.jobNumber,
                appliedFilters.parameterName,
                appliedFilters.rpsApprovedDate,
                appliedFilters.testingDoneDate,
                appliedFilters.bakuMutu,
                appliedFilters.jenisSampel,
              ].some((value) => value.trim() !== '')
            }
            onClick={resetFilters}
            data-testid="laboratory-results-filter-reset"
          >
            Reset filter
          </Button>
        </Stack>
      </CollapsibleListFilters>

      {rowsQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {rowsQ.error instanceof Error ? rowsQ.error.message : 'Gagal memuat daftar.'}
        </Alert>
      ) : null}

      {hideAnalisLabRowEdit && !authLoading && !assignedAnalisTypeLabel ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Analis type belum di-assign ke user ini, jadi daftar hasil uji dikosongkan.
        </Alert>
      ) : null}

      {!rowsQ.isPending && !rowsQ.isFetching && unfilteredRowCount === 0 && !rowsQ.isError ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Belum ada parameter hasil uji. Baris muncul setelah RPS work order disetujui SPV lab
          (Approved RPS date terisi).
        </Alert>
      ) : null}

      {!rowsQ.isPending &&
      !rowsQ.isFetching &&
      unfilteredRowCount > 0 &&
      rowCount === 0 &&
      filtersActive &&
      !(hideAnalisLabRowEdit && !assignedAnalisTypeLabel) &&
      !rowsQ.isError ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Tidak ada baris yang cocok dengan filter.
        </Alert>
      ) : null}

      <DataGrid
        apiRef={gridApiRef}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.row_key}
        loading={rowsQ.isPending || rowsQ.isFetching}
        disableRowSelectionOnClick
        pageSizeOptions={DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS}
        initialState={{
          pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } },
          sorting: { sortModel: [{ field: 'rps_approved_datetime', sort: 'desc' }] },
        }}
        slots={{ pagination: DataGridNumberedPagination }}
        getRowHeight={(params) => (expandedRowKeys.has(String(params.id)) ? 'auto' : 52)}
        getEstimatedRowHeight={() => 280}
        getRowClassName={(params) =>
          expandedRowKeys.has(String(params.id)) ? 'laboratory-result-row--expanded' : ''
        }
        sx={{
          flex: 1,
          minHeight: 360,
          '& .MuiDataGrid-row': {
            position: 'relative',
            zIndex: 0,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-row.laboratory-result-row--expanded': {
            zIndex: 3,
            bgcolor: 'grey.50',
            overflow: 'hidden',
            maxHeight: 'none !important',
          },
          '& .MuiDataGrid-cell': {
            alignItems: 'flex-start',
            py: 0.5,
            overflow: 'hidden',
          },
          '& .MuiDataGrid-row.laboratory-result-row--expanded .MuiDataGrid-cell': {
            bgcolor: 'grey.50',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            whiteSpace: 'normal',
          },
        }}
      />

      <LaboratoryResultEditDialog
        open={Boolean(editTarget)}
        mode={editTarget?.mode ?? 'workOrder'}
        row={editTarget?.row ?? null}
        relatedRows={relatedParameterRows}
        onClose={() => setEditTarget(null)}
        onSaved={(workOrderDocumentIds) => {
          void qc.invalidateQueries({ queryKey: queryKeys.laboratoryResultGridRowsPrefix(jwt) });
          for (const workOrderDocumentId of workOrderDocumentIds) {
            void qc.invalidateQueries({
              queryKey: queryKeys.laboratoryResultWorkOrderForm(jwt, workOrderDocumentId),
            });
          }
        }}
      />

      <Dialog
        open={Boolean(previewWoDocId)}
        onClose={() => {
          setPreviewWoDocId(null);
          setPreviewData(null);
          setPreviewError(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Preview — Laboratory result (LHU)</DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : null}
          {previewError ? <Alert severity="error">{previewError}</Alert> : null}
          {previewData ? <WorkOrderPreviewContent data={previewData} /> : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function WorkOrderPreviewContent({ data }: { data: LaboratoryResultWorkOrderPreviewData }) {
  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Quotation → Work order → Berita acara → Hasil laboratorium
      </Typography>
      {data.quotation ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Quotation
          </Typography>
          <Typography variant="body2">
            {data.quotation.quotation_number} — {data.quotation.customer_name || '—'}
          </Typography>
        </Box>
      ) : null}
      {data.work_order ? (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Work order
          </Typography>
          <Typography variant="body2">{data.work_order.label}</Typography>
        </Box>
      ) : null}

      {data.berita_acara_sections.length === 0 ? (
        <Alert severity="info">Belum ada berita acara dengan status ready.</Alert>
      ) : (
        data.berita_acara_sections.map((section) => (
          <Box key={section.berita_acara_document_id}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {section.row_label}
            </Typography>
            <Typography variant="body2">{section.location_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {section.location_address}
            </Typography>
            {section.preservation_locations.length > 0 ? (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Pengawetan sample
                </Typography>
                {section.preservation_locations.map((loc, i) => (
                  <Typography key={i} variant="body2">
                    {loc.preservation_material_name || loc.parameter_name}
                  </Typography>
                ))}
              </Box>
            ) : null}
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Hasil uji
              </Typography>
              {section.laboratory_result_items.map((item, i) => (
                <Typography key={i} variant="body2" sx={{ '& .katex-html': { whiteSpace: 'normal' } }}>
                  <strong>
                    <ChemInline text={item.parameter_name} />
                  </strong>{' '}
                  = {item.result || '—'}
                </Typography>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Stack>
  );
}
